import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { BatchModification } from '../controls/inline-editable-table/models/batch-modifications';
import { BatchResult } from '../controls/inline-editable-table/models/batch-result';
import { BatchEditableTableServiceInterface } from '../controls/inline-editable-table/services/batch-editable-table-service-interface';
import { BoxAccessibility } from '../models/box-accessibility';
import { Group } from '../models/group';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';
import { ViewAccessibility } from '../models/view-accessibility';

@Injectable({
    providedIn: 'root',
})
export class GroupsService extends CrudService implements AdvancedService<Group>, BatchEditableTableServiceInterface<Group> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<Group>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        if (context === null) {
            // Appel de la Web Api
            return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<Group>(Group, x);
                    return res;
                })
            );
        } else {
            if (context.property === 'parentGroupId') {
                // Appel de la Web Api
                return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${context.value}/Children`, httpOptions).pipe(
                    map((x) => {
                        var res = Results.getCopyFrom<Group>(Group, x);
                        return res;
                    })
                );
            } else if (context.property === 'userParentsId') {
                // Appel de la Web Api
                return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/users/${context.value}/parents`, httpOptions).pipe(
                    map((x) => {
                        var res = Results.getCopyFrom<Group>(Group, x);
                        return res;
                    })
                );
            } else {
                return of(null);
            }
        }
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/export`, { headers: headers, responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    roots(): Observable<Group[]> {
        // Appel de la Web Api
        return this.http.get<Group[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/Roots`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Group.fromArray(x);
            })
        );
    }

    update(groupId: string, group: Group): Observable<Group> {
        // Appel de la Web Api
        return this.http.put<Group>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}`, group, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Group.from(x);
            })
        );
    }

    reset(groupId: string): Observable<Group> {
        // Appel de la Web Api
        return this.http.delete<Group>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Group.from(x);
            })
        );
    }

    add(group: Group): Observable<Group> {
        // Appel de la Web Api
        return this.http.post<Group>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups`, group, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Group.from(x);
            })
        );
    }

    get(groupId: string): Observable<Group> {
        // Appel de la Web Api
        return this.http.get<Group>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Group.from(x);
            })
        );
    }

    getBoxes(groupId: string): Observable<BoxAccessibility[]> {
        // Appel de la Web Api
        return this.http.get<BoxAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}/Boxes`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    getInheritedBoxes(groupId: string): Observable<BoxAccessibility[]> {
        let options = this.getParameterizedHttpOptions([{ key: 'inheritedOnly', value: 'true' }]);

        // Appel de la Web Api
        return this.http.get<BoxAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}/Boxes`, options).pipe(
            map((x) => {
                return x;
            })
        );
    }

    exportBoxes(groupId: string): Observable<Blob> {
        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}/Boxes/export`, { responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    updateBoxes(groupId: string, boxesAccessibility: BoxAccessibility[]): Observable<boolean> {
        // Appel de la Web Api
        return this.http.put(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}/Boxes`, boxesAccessibility, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return true;
            })
        );
    }

    getViews(groupId: string): Observable<ViewAccessibility[]> {
        // Appel de la Web Api
        return this.http
            .get<ViewAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}/viewsAccessibilities`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    getInheritedViews(groupId: string): Observable<ViewAccessibility[]> {
        let options = this.getParameterizedHttpOptions([{ key: 'inheritedOnly', value: 'true' }]);

        // Appel de la Web Api
        return this.http.get<ViewAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}/ViewsAccessibilities`, options).pipe(
            map((x) => {
                return x;
            })
        );
    }

    exportViews(groupId: string): Observable<Blob> {
        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}/viewsAccessibilities/export`, { responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    updateViews(groupId: string, boxesAccessibility: ViewAccessibility[]): Observable<boolean> {
        // Appel de la Web Api
        return this.http
            .put(this.baseUrl + `/api/Organizations/${this.organizationId}/Groups/${groupId}/viewsAccessibilities`, boxesAccessibility, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    public applyBatchModifications(context: Context, modifications: BatchModification<Group>): Observable<BatchResult[]> {
        let requests = new Array<Observable<BatchResult>>();

        modifications.deletes.forEach((x) => {
            requests.push(this.reset(x.data.id).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        modifications.updates.forEach((x) => {
            requests.push(this.update(x.data.id, x.data).pipe(map((y) => new BatchResult(x.data.editableId, true))));
        });

        modifications.creates.forEach((x) => {
            x.data.parentGroupId = context.value.toString();
            requests.push(this.add(x.data).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        return forkJoin(requests);
    }
}
